/**
 * RegionResults — which region's record a global search is showing, and which
 * regions could not be reached.
 *
 * With several hits this is a switch: picking a region swaps the record the
 * page below renders. Failed regions are called out rather than folded into
 * "not found", since an unreachable region says nothing about whether the
 * entity is there. Renders nothing for a clean single-region search.
 */
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { regionLabel } from 'src/constants';
import { RegionFailure, RegionHit } from 'src/store/slices/regionFanOut';

interface RegionResultsProps {
  hits: RegionHit<unknown>[];
  activeRegion: string | null;
  searchedRegions: string[];
  regionErrors: RegionFailure[];
  onSelectRegion: (region: string) => void;
  /** Entity noun for the copy, e.g. "payment". */
  entity: string;
}

export const RegionResults = ({
  hits, activeRegion, searchedRegions, regionErrors, onSelectRegion, entity,
}: RegionResultsProps) => {
  if (searchedRegions.length <= 1 && regionErrors.length === 0) return null;

  return (
    <SpaceBetween size="s">
      {hits.length > 1 && (
        <SegmentedControl
          selectedId={activeRegion}
          onChange={({ detail }) => onSelectRegion(detail.selectedId)}
          label={`Regions holding this ${entity}`}
          options={hits.map(hit => ({ id: hit.region, text: regionLabel(hit.region) }))}
        />
      )}

      {regionErrors.length > 0 && (
        <Alert
          type="warning"
          header={`${regionErrors.length} of ${searchedRegions.length} regions did not respond`}
        >
          <SpaceBetween size="xxs">
            <Box variant="span">
              {hits.length > 0
                ? `This ${entity} may also exist in a region that failed.`
                : `This ${entity} was not found, but the search was incomplete.`}
            </Box>
            {regionErrors.map(e => (
              <Box key={e.region} fontSize="body-s" color="text-status-error">
                {regionLabel(e.region)}: {e.message}
              </Box>
            ))}
          </SpaceBetween>
        </Alert>
      )}
    </SpaceBetween>
  );
};
